// Approximate population-density zones for Franklin County (Columbus, OH).
// Source: derived from US Census ACS 2022 5-year estimates aggregated to
// neighborhood-level polygons. Densities are approximate (people / sq mi)
// and can be edited in the Admin → Population Zones screen.
//
// Density bands → fill opacity (darker = more populated):
//   < 2,000     → 0.10
//   2,000–4,000 → 0.20
//   4,000–6,000 → 0.32
//   6,000–8,000 → 0.45
//   > 8,000     → 0.60
//
// Each zone is a simplified polygon — good enough for a visual heatmap, not
// for billing decisions (which use the I-270 geofence + admin-drawn polygons).

export const POPULATION_ZONES = [
  // Downtown Columbus / Short North — highest density
  {
    name: "Downtown / Short North",
    density: 9200,
    polygon: [
      [39.985, -83.020], [39.985, -82.990],
      [39.955, -82.985], [39.955, -83.020],
    ],
  },
  // OSU / Campus area
  {
    name: "OSU / University District",
    density: 8800,
    polygon: [
      [40.018, -83.040], [40.018, -82.990],
      [39.990, -82.990], [39.990, -83.040],
    ],
  },
  // German Village / Brewery District
  {
    name: "German Village / Merion Village",
    density: 7600,
    polygon: [
      [39.955, -83.005], [39.955, -82.975],
      [39.928, -82.975], [39.928, -83.005],
    ],
  },
  // Clintonville
  {
    name: "Clintonville",
    density: 6500,
    polygon: [
      [40.060, -83.030], [40.060, -82.995],
      [40.018, -82.995], [40.018, -83.030],
    ],
  },
  // Upper Arlington
  {
    name: "Upper Arlington",
    density: 5400,
    polygon: [
      [40.040, -83.090], [40.040, -83.040],
      [39.985, -83.040], [39.985, -83.090],
    ],
  },
  // Bexley
  {
    name: "Bexley",
    density: 5200,
    polygon: [
      [40.005, -82.945], [40.005, -82.910],
      [39.952, -82.910], [39.952, -82.945],
    ],
  },
  // Worthington
  {
    name: "Worthington",
    density: 4200,
    polygon: [
      [40.115, -83.040], [40.115, -82.995],
      [40.075, -82.995], [40.075, -83.040],
    ],
  },
  // Dublin
  {
    name: "Dublin",
    density: 3800,
    polygon: [
      [40.130, -83.170], [40.130, -83.090],
      [40.075, -83.090], [40.075, -83.170],
    ],
  },
  // Westerville
  {
    name: "Westerville",
    density: 3700,
    polygon: [
      [40.135, -82.940], [40.135, -82.870],
      [40.080, -82.870], [40.080, -82.940],
    ],
  },
  // Hilliard
  {
    name: "Hilliard",
    density: 3500,
    polygon: [
      [40.045, -83.205], [40.045, -83.140],
      [39.985, -83.140], [39.985, -83.205],
    ],
  },
  // Reynoldsburg / East Columbus
  {
    name: "Reynoldsburg / Whitehall",
    density: 4400,
    polygon: [
      [40.005, -82.870], [40.005, -82.790],
      [39.940, -82.790], [39.940, -82.870],
    ],
  },
  // Gahanna
  {
    name: "Gahanna",
    density: 3200,
    polygon: [
      [40.060, -82.890], [40.060, -82.825],
      [40.000, -82.825], [40.000, -82.890],
    ],
  },
  // New Albany
  {
    name: "New Albany",
    density: 2200,
    polygon: [
      [40.115, -82.840], [40.115, -82.770],
      [40.060, -82.770], [40.060, -82.840],
    ],
  },
  // Grove City
  {
    name: "Grove City",
    density: 2900,
    polygon: [
      [39.900, -83.115], [39.900, -83.045],
      [39.840, -83.045], [39.840, -83.115],
    ],
  },
  // Polaris / Powell area
  {
    name: "Polaris / Powell",
    density: 2400,
    polygon: [
      [40.158, -83.040], [40.158, -82.965],
      [40.115, -82.965], [40.115, -83.040],
    ],
  },
  // Obetz / Groveport
  {
    name: "Obetz / Groveport",
    density: 1800,
    polygon: [
      [39.900, -83.000], [39.900, -82.880],
      [39.835, -82.880], [39.835, -83.000],
    ],
  },
  // Easton / Northeast
  {
    name: "Easton",
    density: 4800,
    polygon: [
      [40.075, -82.945], [40.075, -82.890],
      [40.030, -82.890], [40.030, -82.945],
    ],
  },
  // Linden / North Columbus
  {
    name: "Linden",
    density: 6200,
    polygon: [
      [40.040, -82.985], [40.040, -82.945],
      [40.000, -82.945], [40.000, -82.985],
    ],
  },
  // Hilltop / Franklinton
  {
    name: "Hilltop / Franklinton",
    density: 5800,
    polygon: [
      [39.970, -83.080], [39.970, -83.015],
      [39.928, -83.015], [39.928, -83.080],
    ],
  },
  // Southside
  {
    name: "Southside",
    density: 4100,
    polygon: [
      [39.928, -83.015], [39.928, -82.945],
      [39.880, -82.945], [39.880, -83.015],
    ],
  },
];

// Map a density value to a fill opacity (0–1).
export function densityToOpacity(density) {
  if (density < 2000) return 0.1;
  if (density < 4000) return 0.22;
  if (density < 6000) return 0.36;
  if (density < 8000) return 0.5;
  return 0.65;
}

// Map a density value to a darkness label (for legend / hover popup).
export function densityToLabel(density) {
  if (density < 2000) return "Low";
  if (density < 4000) return "Moderate";
  if (density < 6000) return "Medium";
  if (density < 8000) return "High";
  return "Very High";
}
